<template>
  <DetailsSection 
    label="Anreise mit der Bahn"
    type="last"
  >
    <template v-if="hasChildren">
      <FieldLabel>Anzahl Kinder 15 Jahre oder älter</FieldLabel>
      <i
        ref="rail-info-icon"
        class="fa fa-info-circle info-icon color-expinet-blue"
        @mouseover="displayTooltip('rail-info-icon')"
        @mouseleave="hideTooltip('rail-info-icon')"
      />
      <Tooltip 
        :open="tooltips['rail-info-icon'].show" 
        :position="tooltips['rail-info-icon'].pos" 
      >
        <span class="tooltip-text fw-normal">
          Kinder bis einschließlich 14 Jahre reisen kostenfrei mit der Bahn
        </span>
      </Tooltip>
  
      <DropdownField
        field-name="railAdults"
        label=""
        :item-list="getRailAdults"
        :field-value="railAdults.toString()"
        :value-changed="setRailAdultValue"
        class="mb-4"
      />
    </template>

    <ToggleGroup
      width="100%"
      :options="getTransportationRailDirectionOptions()"
      :select="selectTransportationRailDirectionOption"
      :selected-key="railDirection"
      class="d-flex justify-content-center mb-2"
    />

    <div 
      v-if="railDirection === 'both'" 
      class="d-flex flex-column"
    >
      <div class="w-100">
        <radio-buttons 
          v-if="isRoundtripRailAvailable" 
          :items="selectableRailOptionsByDirection" 
          :selected-item="selectedRailOptionId"
          :select="selectRailOption"
          :displayed-label="categoryDescription"
        />

        <div 
          v-if="!!railErrorText" 
          class="rail-panel d-flex align-items-center"
        >
          <div class="w-100 text-center fs-16 color-sachlich-grey">
            {{ railErrorText }}
          </div>
        </div>
      </div>
    </div>

    <div 
      v-if="railDirection === 'outbound'"  
      class="d-flex flex-column"
    >
      <div class="w-100">
        <radio-buttons
          v-if="isOutboundRailAvailable" 
          :items="selectableRailOptionsByDirection" 
          :selected-item="selectedRailOptionId"
          :select="selectRailOption"
          :displayed-label="categoryDescription"
        />

        <div 
          v-if="railErrorText" 
          class="rail-panel d-flex align-items-center"
        >
          <div class="w-100 text-center fs-16 color-sachlich-grey">
            {{ railErrorText }}
          </div>
        </div>
      </div>
    </div>

    <div 
      v-if="railDirection === 'inbound'" 
      class="d-flex flex-column"
    >
      <div class="w-100">
        <radio-buttons
          v-if="isInboundRailAvailable" 
          :items="selectableRailOptionsByDirection" 
          :selected-item="selectedRailOptionId"
          :select="selectRailOption"
          :displayed-label="categoryDescription"
        />

        <div 
          v-if="railErrorText" 
          class="rail-panel d-flex align-items-center"
        >
          <div class="w-100 text-center">
            {{ railErrorText }}
          </div>
        </div>
      </div>
    </div>
  </DetailsSection>
</template>

<script lang="ts">
import { Direction } from '@/bewotec/openapi'
import FieldLabel from '@/components/common/FieldLabel.vue'
import RadioButtons from '@/components/common/RadioButtons.vue'
import Tooltip from '@/components/common/Tooltip.vue'
import { IToggleGroupOption } from '@/components/interfaces'
import { useCruiseStore, useFilterStore, useRailStore, useUiStore } from '@/store'
import { convertToExpitecDirection, mapRailErrorText } from '@/store/details/rail/helper'
import { IFetchRailOption, RailDirection } from '@/store/details/rail/interfaces'
import { IPoint } from '@/store/ui/interfaces'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { children } from '../../../general/master-data'
import DropdownField from '../../common/DropdownField.vue'
import ToggleGroup from '../../common/ToggleGroup.vue'
import DetailsSection from '../DetailsSection.vue'

export default defineComponent({
  components: {
    DetailsSection,
    DropdownField,
    ToggleGroup,
    RadioButtons,
    FieldLabel,
    Tooltip
  },

  computed: {
    ...mapState(useFilterStore, {
      paxConfig: store => store.paxConfig,
    }),
    ...mapState(useCruiseStore, {
      route: store => store.route,
    }),
    ...mapState(useRailStore, {
      selectableRailOptions: store => store.selectableRailOptions,
      railDirection: store => store.railDirection,
    }),
    ...mapState(useUiStore, {
      tooltips: store => store.tooltips,
    }),
    ...mapWritableState(useRailStore, {
      selectedRailOptionId: 'selectedRailOptionId',
      railAdults: 'railAdults',
    }),
    hasChildren() {
      return !!this.paxConfig.children
    },
    getRailAdults() {
      const railAdults = []

      for (let index = 0; index <= this.paxConfig.children; ++index) {
        railAdults.push(children[index])
      }

      return railAdults
    },
    selectableRailOptionsByDirection(): IFetchRailOption[] {
      const direction: Direction = convertToExpitecDirection(this.railDirection)
      return !('error' in this.selectableRailOptions) && !!this.selectableRailOptions[direction]
        ? this.selectableRailOptions[direction]
        : []
    },
    isInboundRailAvailable() {
      return !('error' in this.selectableRailOptions) ? !!this.selectableRailOptions[Direction.R]?.length : false
    },
    isOutboundRailAvailable() {
      return !('error' in this.selectableRailOptions) ? !!this.selectableRailOptions[Direction.O]?.length : false
    },
    isRoundtripRailAvailable() {
      return !('error' in this.selectableRailOptions) ? !!this.selectableRailOptions[Direction.RT]?.length : false
    },
    railErrorText() {
      return mapRailErrorText(this.route, this.selectableRailOptions, this.railDirection)
    },
  },

  methods: {
    ...mapActions(useRailStore, {
      fetchRailOptions: 'fetchRailOptions',
      resetSelectedRailOption: 'resetSelectedRailOption',
      selectRailDirection: 'selectRailDirection',
      setRailOption: 'selectRailOption',
    }),
    ...mapActions(useUiStore, {
      hideTooltip: 'hideTooltip',
      showTooltip: 'showTooltip'
    }),
    setRailAdultValue(_: any, fieldValue: string) {
      this.resetSelectedRailOption()
      this.railAdults = parseInt(fieldValue, 10)
      this.fetchRailOptions()
    },
    selectTransportationRailDirectionOption(option: RailDirection) {
      this.selectRailDirection(option)
    },
    getTransportationRailDirectionOptions(): IToggleGroupOption[] {
      return [
        {
          key: 'both',
          label: 'Hin- und Rückfahrt',
          available: this.isRoundtripRailAvailable,
        },
        {
          key: 'outbound',
          label: 'Hinfahrt',
          available: this.isOutboundRailAvailable,
        },
        {
          key: 'inbound',
          label: 'Rückfahrt',
          available: this.isInboundRailAvailable,
        },
      ]
    },

    selectRailOption(optionId: string) {
      this.selectedRailOptionId = optionId
      this.setRailOption(optionId)
    },

    categoryDescription: (option: IFetchRailOption) => {
      const tariff = option.railTarifCode === 'FLEX' ? 'Flexpreis' : 'Sparpreis'

      return `${option.railClassName} ${tariff}`
    },
    
    displayTooltip(name: string) {
      const refname = this.$refs[name] as HTMLElement

      if (!!refname) {
        const rect = refname.getBoundingClientRect()
        const pos: IPoint = { x: rect.left, y: rect.top }
        this.showTooltip({ name, pos })
      }
    },
  },
})
</script>

<style lang="scss" scoped>
  .rail-panel {
    min-height: 80px;
  }
</style>
